import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import ProductWalkthrough, { walkthroughData } from "@/components/home/ProductWalkthrough";
import { ParticleField, DataStream, MorphBlob, NodeNetwork, FlowLines } from "@/components/effects/DynamicBackgrounds";
import { SEOHead, buildProductLD } from "@/components/seo/SEOHead";
import { 
  Database, 
  BarChart3, 
  FileText, 
  ArrowRight, 
  CheckCircle,
  Zap,
  Shield,
  Clock,
  Globe,
  TrendingUp,
  Users,
  FileSearch,
  Brain,
  Upload,
  RefreshCw,
  AlertTriangle,
  Play,
  Lock,
  Bell,
  FileCheck,
  Workflow,
  PenTool,
  Search,
  Mail,
  LayoutDashboard,
  PieChart,
  Target,
  Sparkles,
  Scale,
  Layers,
  GitCompare,
  Calendar,
  Download,
  Settings,
  Eye,
  Edit,
  ClipboardList,
  Building2,
  DollarSign,
  LineChart
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const products = {
  "data-refinery": {
    id: "data-refinery",
    name: "Procureaide DataRefinery",
    tagline: "Fix Your Data Before It Fails You",
    icon: Database,
    color: "#00B4D8",
    description: "AI-powered data ingestion, cleansing, and classification. Turn messy procurement data into clean, analysis-ready intelligence with multi-level categorization and supplier normalization.",
    heroImage: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/4htx5oil_DR2.jpeg",
    
    flowTitle: "From Chaos to Clarity",
    flowSteps: [
      { step: "Upload", description: "CSV, Excel, JSON — any format" },
      { step: "AI Processes", description: "Cleanses, dedupes, classifies" },
      { step: "Categorized", description: "L1→L4 taxonomy applied" },
      { step: "Ready", description: "Analysis-ready, exportable" }
    ],
    
    beforeAfter: {
      before: ["Fragmented supplier names", "Inconsistent categories", "Duplicate records", "Missing data fields"],
      after: ["Normalized supplier master", "L1-L4 standardized taxonomy", "Single source of truth", "Quality scored data"]
    },
    
    featureCategories: [
      {
        title: "Data Ingestion",
        icon: Upload,
        features: [
          "Manual upload (CSV, Excel .xlsx/.xls, JSON)",
          "Flexible column mapping during upload",
          "Multi-file upload to single session (append)",
          "Replace existing session data",
          "Automated Ingest API (connectors)",
          "Persistent Supplier Merge Rules auto-applied"
        ]
      },
      {
        title: "AI-Powered Processing",
        icon: Zap,
        features: [
          "Data cleansing & standardization",
          "Multi-item description splitting",
          "Deduplication detection",
          "Async background processing (3000+ records)",
          "Progress tracking with polling",
          "3-retry logic with exponential backoff",
          "Parallel AI calls with bulk DB writes"
        ]
      },
      {
        title: "AI Classification",
        icon: Brain,
        features: [
          "Multi-level categorization (L1 → L4)",
          "Custom taxonomy support (bespoke CRUD)",
          "UNSPSC taxonomy support",
          "Cash payment detection",
          "Reclassify Incomplete — one-click fix",
          "Bulk reclassify selected records",
          "Classification Rules engine",
          "Incremental DB writes (survives restarts)"
        ]
      },
      {
        title: "Supplier Management",
        icon: Building2,
        features: [
          "Supplier normalization with fuzzy matching",
          "Merge suppliers (updates all records)",
          "\"Do Not Merge\" decisions persisted",
          "Persistent merge rules for future uploads",
          "Supplier profiles & enrichment"
        ]
      },
      {
        title: "Analytics Dashboard",
        icon: LayoutDashboard,
        features: [
          "Spend by category breakdown",
          "Top 10 Suppliers by Spend chart",
          "Cash Transaction Risk panel",
          "Dynamic currency display",
          "Multi-tenant organization name"
        ]
      },
      {
        title: "ML Performance & Insights",
        icon: LineChart,
        features: [
          "ML Performance dashboard",
          "Confidence scores & pipeline progress",
          "ML Insights (correction trends)",
          "Impact Analysis (category accuracy)",
          "User corrections tracking"
        ]
      },
      {
        title: "Processed Data",
        icon: Database,
        features: [
          "Server-side pagination (any size)",
          "Server-side search & filters",
          "Filter by L1, supplier, confidence, cash",
          "Expandable row details",
          "Bulk selection & reclassify",
          "CSV export"
        ]
      },
      {
        title: "External API (SpendLens)",
        icon: Globe,
        features: [
          "API key generation & management",
          "Pull API: list sessions, fetch data",
          "X-API-Key header authentication",
          "Multi-tenant scoped responses",
          "Full data fields in response"
        ]
      },
      {
        title: "Security & Admin",
        icon: Lock,
        features: [
          "JWT authentication",
          "RBAC (Admin, Analyst, Viewer)",
          "Admin panel: user invite, roles, org CRUD",
          "Forgot Password (token-based reset)",
          "Activity Audit Log (filterable, paginated)"
        ]
      },
      {
        title: "Category Taxonomy",
        icon: Layers,
        features: [
          "Custom taxonomy CRUD",
          "Create/edit/delete categories",
          "Bespoke taxonomy selection",
          "UNSPSC mapping support"
        ]
      }
    ],
    
    outcomes: [
      { metric: "80%", description: "Reduction in data prep time" },
      { metric: "L1→L4", description: "Multi-level classification" },
      { metric: "Hours", description: "Not weeks to clean data" }
    ]
  },
  
  "spendlens": {
    id: "spendlens",
    name: "Procureaide SpendLens",
    tagline: "From Upload to Insight in Minutes",
    icon: BarChart3,
    color: "#10B981",
    description: "Instant spend analytics with AI-driven insights. Upload in the morning, present to the board by afternoon.",
    heroImage: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/c8uqt9c4_02-dashboard.png",
    
    flowTitle: "Same-Day Intelligence",
    flowSteps: [
      { step: "Upload CSV", description: "Your spend data, any format" },
      { step: "AI Analyzes", description: "Patterns, anomalies, opportunities" },
      { step: "Dashboard", description: "Interactive visualizations appear" },
      { step: "Insights", description: "AI-generated action plans" }
    ],
    
    sampleInsights: [
      "Top 5 suppliers driving 60% of spend",
      "Maverick spend identified: $240K off-contract",
      "Savings opportunity: 8% in IT category",
      "3 suppliers flagged for single-source risk"
    ],
    
    featureCategories: [
      {
        title: "Spend Visibility & Dashboards",
        icon: LayoutDashboard,
        features: [
          "12+ analytics pages",
          "Dashboard with role-aware stats",
          "Spend Analysis & Distribution",
          "Category Console",
          "Commonality Analysis",
          "Comparative Analytics",
          "P2P Analytics"
        ]
      },
      {
        title: "AI-Powered Insights",
        icon: Brain,
        features: [
          "AI contextual insights",
          "Savings identification",
          "AI action plans",
          "Category strategies",
          "SRM tips & recommendations",
          "Industry benchmarking"
        ]
      },
      {
        title: "Supplier Management",
        icon: Building2,
        features: [
          "Full supplier directory",
          "Risk scoring",
          "Kraljic matrix positioning",
          "Duplicate detection & normalization",
          "Supplier scorecards",
          "AI SRM tips"
        ]
      },
      {
        title: "Spend Forecasting",
        icon: LineChart,
        features: [
          "AI-powered 3/6/12-month forecasts",
          "Drilldown filters",
          "Trend analysis",
          "Budget variance tracking"
        ]
      },
      {
        title: "P2P Analytics",
        icon: Workflow,
        features: [
          "PO/PR/GRN/Invoice coverage",
          "Cycle time analysis",
          "Maverick spend detection",
          "Aging bucket analysis"
        ]
      },
      {
        title: "Reports & Export",
        icon: Download,
        features: [
          "12 report types",
          "Scheduled PDF generation",
          "AI insights in reports",
          "CSV, JSON, PDF export"
        ]
      }
    ],
    
    outcomes: [
      { metric: "15%", description: "Average savings identified" },
      { metric: "Minutes", description: "To first insight" },
      { metric: "12+", description: "Analytics dashboards" }
    ]
  },
  
  "contrack": {
    id: "contrack",
    name: "Procureaide ConTrack",
    tagline: "Upload a Contract. Get Answers in 20 Seconds.",
    icon: FileText,
    color: "#8B5CF6",
    description: "AI contract intelligence with risk analysis across 40+ jurisdictions. Full analysis in seconds, not days.",
    heroImage: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/uiewdd67_CT2.jpeg",
    
    flowTitle: "Contract Intelligence, Instantly",
    flowSteps: [
      { step: "Upload Contract", description: "PDF, DOCX — any contract" },
      { step: "AI Scans", description: "Reads every clause in ~20 seconds" },
      { step: "Risks Flagged", description: "Issues ranked by severity" },
      { step: "Summary", description: "Executive brief generated" }
    ],
    
    beforeAfter: {
      before: ["20-page contract", "Days of manual review", "Missed risks", "No standardization"],
      after: ["1-page AI summary", "20-second analysis", "All risks flagged", "Approval recommendation"]
    },
    
    featureCategories: [
      {
        title: "AI-Powered Intelligence",
        icon: Brain,
        features: [
          "AI-Assisted Drafting from prompts",
          "AI Deliverables & KPIs Extraction",
          "AI Contract Risk Review (40+ jurisdictions)",
          "AI Executive Summary for Approvers",
          "AI Obligation & Milestone Extraction",
          "AI Supplier Contract Review (~20 seconds)",
          "Risk score with clause-level analysis",
          "Missing standard clauses detection"
        ]
      },
      {
        title: "Governed Approval Workflow",
        icon: Workflow,
        features: [
          "Multi-Stage Pipeline (Review → Approval)",
          "Segregation of Duty enforcement",
          "Auto-suggest reviewers/approvers",
          "Version Comparison for Reviewers",
          "Delegation of Authority with audit trail",
          "Cancel/Recall & Resubmit workflows"
        ]
      },
      {
        title: "Contract Lifecycle Management",
        icon: FileCheck,
        features: [
          "Full Rich-Text Editor",
          "Template Builder (18 section types)",
          "Clause Library management",
          "Addendum Management with workflows",
          "Version History with line-by-line diff",
          "Contract Register with CSV export"
        ]
      },
      {
        title: "Compliance & Monitoring",
        icon: Shield,
        features: [
          "Obligations & Milestones Tracking",
          "Compliance Score visualization",
          "Overdue auto-detection",
          "Email reminders",
          "Due date management"
        ]
      },
      {
        title: "E-Signature & Execution",
        icon: PenTool,
        features: [
          "DocuSign Integration",
          "Internal & external signing",
          "Public signing page",
          "Fully executed status tracking",
          "PDF draft sharing"
        ]
      },
      {
        title: "Security & Audit",
        icon: Lock,
        features: [
          "RBAC (7 role levels)",
          "Granular page permissions",
          "GDPR Compliance",
          "End-to-end audit trail",
          "Session timeout & account lockout",
          "Security headers (HSTS, CSP)"
        ]
      },
      {
        title: "Reporting & Analytics",
        icon: PieChart,
        features: [
          "Role-aware dashboard",
          "Approval cycle time analytics",
          "Expiring contracts watchlist",
          "Report generator (CSV, JSON, PDF)",
          "Status & type distributions"
        ]
      },
      {
        title: "Collaboration",
        icon: Bell,
        features: [
          "In-app notification inbox",
          "11 notification types",
          "Dual delivery (in-app + email)",
          "Share draft with supplier",
          "Real-time polling updates"
        ]
      }
    ],
    
    outcomes: [
      { metric: "~20s", description: "Full contract analysis" },
      { metric: "40+", description: "Jurisdictions covered" },
      { metric: "90%", description: "Faster review time" }
    ]
  }
};

export const ProductsPage = () => {
  const { productId } = useParams();
  const product = productId ? products[productId] : null;

  if (!product) {
    return <ProductsOverview />;
  }

  return (
    <div className="bg-[#020617] pt-20">
      <SEOHead
        title={product.name}
        description={product.tagline}
        path={`/products/${productId}`}
        jsonLd={buildProductLD({ name: product.name, description: product.tagline, path: `/products/${productId}` })}
      />
      {/* Hero */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern-animated opacity-30" />
        <ParticleField count={18} />
        <DataStream count={5} className="opacity-30" />
        <MorphBlob color={`${product.color}10`} size="500px" className="top-[10%] right-[-10%]" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              <motion.div 
                variants={fadeInUp}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-6"
                style={{ borderColor: `${product.color}40`, backgroundColor: `${product.color}10` }}
              >
                <product.icon className="w-4 h-4" style={{ color: product.color }} />
                <span className="text-sm" style={{ color: product.color }}>Product Module</span>
              </motion.div>
              <motion.h1 
                variants={fadeInUp}
                className="text-4xl sm:text-5xl font-bold text-[#F8FAFC] leading-tight tracking-tight mb-4"
              >
                {product.name}
              </motion.h1>
              <motion.p 
                variants={fadeInUp}
                className="text-xl mb-4"
                style={{ color: product.color }}
              >
                {product.tagline}
              </motion.p>
              <motion.p 
                variants={fadeInUp}
                className="text-lg text-[#94A3B8] mb-8"
              >
                {product.description}
              </motion.p>
              <motion.div 
                variants={fadeInUp}
                className="flex flex-wrap gap-4"
              >
                <Link to="/#demo">
                  <Button 
                    size="lg"
                    className="font-bold hover:scale-105 transition-all px-8"
                    style={{ 
                      backgroundColor: product.color, 
                      color: "#020617",
                      boxShadow: `0 0 20px ${product.color}40`
                    }}
                    data-testid={`${product.id}-try-demo-btn`}
                  >
                    <Play className="mr-2 w-5 h-5" />
                    Preview Platform
                  </Button>
                </Link>
                <a href="mailto:info@procureaide.com">
                  <Button 
                    size="lg"
                    variant="outline"
                    className="px-8"
                    style={{ borderColor: product.color, color: product.color }}
                    data-testid={`${product.id}-see-action-btn`}
                  >
                    Book Demo
                  </Button>
                </a>
              </motion.div>

              {/* Quick Outcomes */}
              <motion.div 
                variants={fadeInUp}
                className="mt-8 pt-8 border-t border-[#00B4D8]/10 grid grid-cols-3 gap-4"
              >
                {product.outcomes.map((outcome, i) => (
                  <div key={i}>
                    <div className="text-2xl font-bold" style={{ color: product.color }}>{outcome.metric}</div>
                    <p className="text-xs text-[#64748B]">{outcome.description}</p>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative"
            >
              <div 
                className="rounded-2xl overflow-hidden border"
                style={{ borderColor: `${product.color}30` }}
              >
                <img 
                  src={product.heroImage} 
                  alt={product.name}
                  className="w-full h-[400px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-transparent to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Flow Section */}
      <section className="py-20 bg-[#0B1426]/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight mb-4"
            >
              {product.flowTitle}
            </motion.h2>
          </motion.div>

          <div className="relative">
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#00B4D8]/50 to-transparent transform -translate-y-1/2" />
            
            <div className="grid md:grid-cols-4 gap-6">
              {product.flowSteps.map((step, index) => (
                <motion.div
                  key={step.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="relative text-center"
                >
                  <div 
                    className="relative z-10 w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 border-2 text-xl font-bold"
                    style={{ 
                      backgroundColor: `${product.color}15`,
                      borderColor: product.color,
                      color: product.color
                    }}
                  >
                    {index + 1}
                  </div>
                  <h3 className="text-lg font-semibold text-[#F8FAFC] mb-1">{step.step}</h3>
                  <p className="text-sm text-[#94A3B8]">{step.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Platform Walkthrough - if screenshots available */}
      {walkthroughData[product.id]?.steps?.length > 0 && (
        <section className="py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="text-center mb-12"
            >
              <motion.h2 
                variants={fadeInUp}
                className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight mb-4"
              >
                See {product.name.split(' ').pop()} in Action
              </motion.h2>
              <motion.p 
                variants={fadeInUp}
                className="text-[#94A3B8] max-w-2xl mx-auto"
              >
                Walk through the real platform — from login to insights.
              </motion.p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-[#111F36] border border-[#00B4D8]/20 rounded-2xl p-6 sm:p-8"
            >
              <ProductWalkthrough productId={product.id} />
            </motion.div>
          </div>
        </section>
      )}

      {/* Sample Insights or Before/After */}
      {product.sampleInsights ? (
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="text-center mb-12"
            >
              <motion.h2 
                variants={fadeInUp}
                className="text-3xl font-bold text-[#F8FAFC] tracking-tight mb-4"
              >
                Example Insights You Get
              </motion.h2>
            </motion.div>

            <div className="grid sm:grid-cols-2 gap-4">
              {product.sampleInsights.map((insight, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: i * 0.1 }}
                  className="bg-[#0B1426] border border-[#10B981]/20 rounded-lg p-4 flex items-start gap-3"
                >
                  <CheckCircle className="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5" />
                  <span className="text-[#F8FAFC]">{insight}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      ) : product.beforeAfter ? (
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="text-center mb-12"
            >
              <motion.h2 
                variants={fadeInUp}
                className="text-3xl font-bold text-[#F8FAFC] tracking-tight mb-4"
              >
                Before & After
              </motion.h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-[#0B1426] border border-[#EF4444]/20 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-[#EF4444] mb-4">Before</h3>
                <ul className="space-y-3">
                  {product.beforeAfter.before.map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-[#94A3B8]">
                      <div className="w-2 h-2 rounded-full bg-[#EF4444]" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-[#0B1426] border border-[#10B981]/20 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-[#10B981] mb-4">After</h3>
                <ul className="space-y-3">
                  {product.beforeAfter.after.map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-[#F8FAFC]">
                      <CheckCircle className="w-5 h-5 text-[#10B981]" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* Feature Categories - NEW COMPREHENSIVE SECTION */}
      <section className="py-20 bg-[#0B1426]/50 relative overflow-hidden">
        <NodeNetwork count={10} className="opacity-20" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight mb-4"
            >
              Complete Feature Set
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-[#94A3B8] max-w-2xl mx-auto"
            >
              Everything you need for comprehensive {
                { contrack: "contract management", spendlens: "spend analytics", "data-refinery": "data management" }[product.id] || "data management"
              }
            </motion.p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {product.featureCategories.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="bg-[#111F36] border border-[#00B4D8]/10 rounded-xl p-6 hover:border-[#00B4D8]/30 transition-colors"
              >
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${product.color}15` }}
                >
                  <category.icon className="w-6 h-6" style={{ color: product.color }} />
                </div>
                <h3 className="text-lg font-semibold text-[#F8FAFC] mb-4">{category.title}</h3>
                <ul className="space-y-2">
                  {category.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#94A3B8]">
                      <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: product.color }} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight mb-4"
            >
              Ready to See {product.name.split(' ').pop()} in Action?
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-[#94A3B8] mb-8"
            >
              Preview the platform walkthrough or book a personalized demo.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4">
              <Link to="/#demo">
                <Button 
                  size="lg"
                  className="font-bold hover:scale-105 transition-all px-8"
                  style={{ 
                    backgroundColor: product.color, 
                    color: "#020617",
                    boxShadow: `0 0 20px ${product.color}40`
                  }}
                >
                  Preview Platform
                </Button>
              </Link>
              <a href="mailto:info@procureaide.com">
                <Button 
                  size="lg"
                  variant="outline"
                  className="px-8"
                  style={{ borderColor: product.color, color: product.color }}
                >
                  Book Demo
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

// Products Overview Component
const ProductsOverview = () => {
  const productList = Object.values(products);

  return (
    <div className="bg-[#020617] pt-20">
      <SEOHead
        title="Products"
        description="Explore ProcureAide's modular product suite: DataRefinery for data cleansing, SpendLens for spend analytics, and ConTrack for contract lifecycle management. Purchase independently or together."
        path="/products"
      />
      {/* Hero */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern-animated opacity-30" />
        <ParticleField count={20} />
        <MorphBlob color="rgba(0,180,216,0.06)" size="500px" className="top-[5%] right-[-10%]" />
        <MorphBlob color="rgba(10,36,99,0.1)" size="400px" className="bottom-[5%] left-[-10%]" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.p 
              variants={fadeInUp}
              className="text-xs uppercase tracking-[0.2em] text-[#00B4D8] mb-4"
            >
              The Platform
            </motion.p>
            <motion.h1 
              variants={fadeInUp}
              className="text-4xl sm:text-5xl font-bold text-[#F8FAFC] leading-tight tracking-tight mb-6"
            >
              Choose What You Need
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              className="text-lg text-[#94A3B8] mb-4"
            >
              Each module is a standalone product — fully independent and ready to deliver value on its own.
            </motion.p>
            <motion.p 
              variants={fadeInUp}
              className="text-[#94A3B8]"
            >
              Go for what suits your needs and organisational objectives. 
              You don't have to commit to the full platform — start with one module 
              and expand when you're ready.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {productList.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? "lg:flex-row-reverse" : ""
                }`}
              >
                <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                  <div 
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-4"
                    style={{ borderColor: `${product.color}40`, backgroundColor: `${product.color}10` }}
                  >
                    <product.icon className="w-4 h-4" style={{ color: product.color }} />
                    <span className="text-sm" style={{ color: product.color }}>Product Module</span>
                  </div>
                  <h2 className="text-3xl font-bold text-[#F8FAFC] mb-2">{product.name}</h2>
                  <p className="text-lg mb-4" style={{ color: product.color }}>{product.tagline}</p>
                  <p className="text-[#94A3B8] mb-6">{product.description}</p>
                  
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    {product.outcomes.map((outcome, i) => (
                      <div key={i}>
                        <div className="text-xl font-bold" style={{ color: product.color }}>{outcome.metric}</div>
                        <p className="text-xs text-[#64748B]">{outcome.description}</p>
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-3">
                    <Link to={`/products/${product.id}`}>
                      <Button 
                        className="font-semibold"
                        style={{ backgroundColor: product.color, color: "#020617" }}
                        data-testid={`products-${product.id}-btn`}
                      >
                        View All Features
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                    <Link to="/#demo">
                      <Button 
                        variant="outline"
                        style={{ borderColor: product.color, color: product.color }}
                      >
                        <Play className="mr-2 w-4 h-4" />
                        Preview Platform
                      </Button>
                    </Link>
                  </div>
                </div>
                <div className={index % 2 === 1 ? "lg:order-1" : ""}>
                  <div 
                    className="rounded-2xl overflow-hidden border h-[350px]"
                    style={{ borderColor: `${product.color}30` }}
                  >
                    <img 
                      src={product.heroImage} 
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0B1426]/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-[#F8FAFC] mb-4">
            Not Sure Where to Start?
          </h2>
          <p className="text-[#94A3B8] mb-3">
            You don't need all three. Pick the module that matches your immediate challenge 
            and see results from day one.
          </p>
          <p className="text-sm text-[#64748B] mb-8">
            Need help deciding? Our team can recommend the right fit based on your organisation's goals.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/#demo">
              <Button 
                size="lg"
                className="bg-[#00B4D8] text-[#020617] font-bold hover:bg-[#0096B4] hover:scale-105 transition-all shadow-[0_0_20px_rgba(0,180,216,0.3)] px-8"
                data-testid="products-try-demo-btn"
              >
                Preview Platform
              </Button>
            </Link>
            <a href="mailto:info@procureaide.com">
              <Button 
                size="lg"
                variant="outline"
                className="border-[#00B4D8] text-[#00B4D8] hover:bg-[#00B4D8]/10 px-8"
                data-testid="products-cta-btn"
              >
                Talk to Us
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductsPage;
